<template>
  <div>
    <header class="topbar">
      <div class="nav-left">
        <img class="nav-logo" src="@/assets/home/nav-logo.svg" alt="OpenClaw" />
      </div>
      <div class="nav-right">
        <div class="wallet-chip" @click="onWalletChipClick">
          <span class="wallet-chip-text">{{ walletAddress ? shortAddress(walletAddress) : connectWalletLabel }}</span>
        </div>
        <div class="lang-wrap" ref="langWrap">
          <button class="icon-btn" type="button" @click="showLangMenu = !showLangMenu">
            <img class="icon-lang" src="@/assets/home/icon-language.svg" alt="language" />
          </button>
          <div class="lang-menu" v-if="showLangMenu">
            <div class="lang-item" @click="changeLang('en')">English</div>
            <div class="lang-item" @click="changeLang('zh')">繁體中文</div>
            <div class="lang-item" @click="changeLang('ko')">한국어</div>
            <div class="lang-item" @click="changeLang('ja')">日本語</div>
            <div class="lang-item" @click="changeLang('vi')">Tiếng Việt</div>
            <div class="lang-item" @click="changeLang('id')">Bahasa Indonesia</div>
          </div>
        </div>
      </div>
    </header>

    <div class="bind-ref-mask" v-if="showBindRefModal">
      <div class="bind-ref-modal">
        <div class="bind-ref-title">
          <img class="bind-ref-title-icon" src="@/assets/modal_logo.png" alt="" />
          <span>{{ t.bindRefTitle }}</span>
        </div>
        <input
          class="bind-ref-input"
          v-model="refInput"
          :placeholder="t.bindRefPlaceholder"
        />
        <button class="bind-ref-confirm-btn" type="button" :disabled="bindRefLoading" @click="confirmBindRef">
          {{ bindRefLoading ? '...' : t.bindRefConfirm }}
        </button>
        <button class="bind-ref-skip-btn" type="button" @click="skipBindRef">{{ t.bindRefSkip }}</button>
      </div>
    </div>

    <div class="topbar-toast" v-if="toastVisible">{{ toastMsg }}</div>
  </div>
</template>

<script>
import { wallet } from '@/store/wallet'
import { getMessages } from '@/i18n'
import { getMessage, signIn, getUser, signOut, bindRef } from '@/api/auth'

let walletInitialized = false

export default {
  name: 'AppTopbar',
  data() {
    return {
      showLangMenu: false,
      lang: localStorage.getItem('lang') || 'en',
      showBindRefModal: false,
      refInput: '',
      bindRefLoading: false,
      toastVisible: false,
      toastMsg: '',
    }
  },
  computed: {
    walletAddress() {
      return wallet.address
    },
    connectWalletLabel() {
      return getMessages(this.lang).connectWallet
    },
    t() {
      return getMessages(this.lang)
    },
    isLoggedIn() {
      return wallet.isLoggedIn
    }
  },
  watch: {
    isLoggedIn(val) {
      if (!val && wallet.address && !wallet.isLoggingIn) {
        this.siweLogin()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this._onDocClick)
    if (window.ethereum && !walletInitialized) {
      walletInitialized = true
      this.initWallet()
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this._onDocClick)
  },
  methods: {
    async initWallet() {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' })
        if (accounts.length > 0) {
          wallet.address = accounts[0]
          this.tryRestoreSession()
        }
        window.ethereum.on('accountsChanged', async accounts => {
          const newAddress = accounts.length > 0 ? accounts[0] : ''
          if (newAddress.toLowerCase() === wallet.address.toLowerCase()) return
          if (wallet.isLoggedIn) {
            try { await signOut() } catch (e) { /* ignore */ }
          }
          wallet.address = newAddress
          wallet.isLoggedIn = false
          if (wallet.address) this.siweLogin()
        })
      } catch (e) {
        console.error('initWallet failed', e)
      }
    },
    async tryRestoreSession() {
      if (!wallet.address) return
      try {
        const res = await getUser()
        if (res.data && res.data.code === 401) {
          this.siweLogin()
          return
        }
        const user = res.data && (res.data.data || res.data)
        const sessionAddr = user && (user.address || user.wallet_address)
        if (sessionAddr && sessionAddr.toLowerCase() !== wallet.address.toLowerCase()) {
          try { await signOut() } catch (e) { /* ignore */ }
          wallet.isLoggedIn = false
          this.siweLogin()
          return
        }
        wallet.isLoggedIn = true
        this.checkRefBound(res)
      } catch (e) {
        this.siweLogin()
      }
    },
    async connectWallet() {
      if (!window.ethereum) return
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' })
        wallet.address = accounts[0]
        this.$emit('wallet-connected', accounts[0])
        this.siweLogin()
      } catch (e) {
        console.error('connectWallet failed', e)
      }
    },
    async siweLogin() {
      if (wallet.isLoggedIn || !wallet.address || wallet.isLoggingIn) return
      wallet.isLoggingIn = true
      try {
        const chainId = String(window.ethereum.chainId ? parseInt(window.ethereum.chainId) : (process.env.VUE_APP_NETWORK_ID || '173'))
        const { data } = await getMessage(wallet.address, chainId)
        const message = typeof data === 'string' ? data : data.message || data
        const signature = await window.ethereum.request({ method: 'personal_sign', params: [message, wallet.address] })
        const ref = localStorage.getItem('ref_address') || undefined
        await signIn(signature, ref)
        localStorage.removeItem('ref_address')
        wallet.isLoggedIn = true
        const res = await getUser()
        this.checkRefBound(res)
      } catch (e) {
        console.error('SIWE login failed', e)
        wallet.isLoggedIn = false
      } finally {
        wallet.isLoggingIn = false
      }
    },
    checkRefBound(res) {
      const user = res && res.data && (res.data.data || res.data)
      if (!user || !user.ref_address) {
        const urlRef = new URLSearchParams(window.location.search).get('ref')
        this.refInput = urlRef || localStorage.getItem('ref_address') || ''
        this.showBindRefModal = true
        wallet.needsRef = true
      } else {
        wallet.needsRef = false
      }
    },
    async confirmBindRef() {
      if (!this.refInput.trim()) return
      if (this.refInput.trim().toLowerCase() === wallet.address.toLowerCase()) {
        this.showToast(this.t.bindRefSelf)
        return
      }
      this.bindRefLoading = true
      try {
        await bindRef(this.refInput.trim())
        wallet.needsRef = false
        this.showBindRefModal = false
        this.showToast(this.t.bindRefSuccess)
      } catch (e) {
        console.error('bindRef failed', e)
        this.showToast(this.t.bindRefFailed)
      } finally {
        this.bindRefLoading = false
      }
    },
    skipBindRef() {
      this.showBindRefModal = false
      wallet.needsRef = false
    },
    showToast(msg) {
      this.toastMsg = msg
      this.toastVisible = true
      clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastVisible = false }, 2000)
    },
    onWalletChipClick() {
      if (!this.walletAddress) this.connectWallet()
    },
    shortAddress(addr) {
      return addr.slice(0, 4) + '...' + addr.slice(-4)
    },
    changeLang(lang) {
      this.lang = lang
      this.showLangMenu = false
      localStorage.setItem('lang', lang)
      this.$emit('lang-change', lang)
    },
    _onDocClick(e) {
      if (this.$refs.langWrap && !this.$refs.langWrap.contains(e.target)) {
        this.showLangMenu = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.nav-logo {
  width: 110px;
  height: 22px;
  object-fit: contain;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wallet-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  background: linear-gradient(127deg, rgba(237, 35, 60, 1) 0%, rgba(237, 35, 60, 0) 49%), rgba(255, 255, 255, 0.05);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    background: linear-gradient(134deg, #fff 2%, transparent 16%),
      linear-gradient(135deg, rgba(243, 40, 90, 1) 2%, rgba(255, 255, 255, 0.1) 98%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.wallet-chip-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
}

.lang-wrap {
  position: relative;
}

.icon-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-lang {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.lang-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 150px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: #1e0d1c;
  z-index: 50;
  overflow: hidden;
}

.lang-item {
  padding: 10px 14px;
  font-size: 14px;
  color: #e5dce8;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child {
    border-bottom: none;
  }
  &:active {
    background: rgba(255, 255, 255, 0.06);
  }
}

.bind-ref-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-sizing: border-box;
}

.bind-ref-modal {
  width: 100%;
  max-width: 320px;
  height: 280px;
  border-radius: 14px;
  padding: 14px 14px 16px;
  padding-top: 62px;
  box-sizing: border-box;
  background: url('~@/assets/modal_back.png') no-repeat center;
  background-size: 100% 100%;
}

.bind-ref-title {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  font-size: 18px;
  color: #ffdae3;
}

.bind-ref-title-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
}

.bind-ref-input {
  margin-top: 16px;
  width: 100%;
  height: 42px;
  border: none;
  outline: none;
  border-radius: 8px;
  padding: 0 10px;
  box-sizing: border-box;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
  margin-bottom: 12px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }
}

.bind-ref-confirm-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
  background: linear-gradient(180deg, #ff3f66, #e80d40);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.bind-ref-skip-btn {
  margin-top: 10px;
  width: 100%;
  height: 36px;
  border: none;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  background: transparent;
  cursor: pointer;
}

.topbar-toast {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1000;
}
</style>
